import React, {useEffect, useState, useCallback} from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Container,
  InputLabel,
  Alert,
  Slider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import {CloudUpload, Close, AspectRatio} from "@mui/icons-material";
import Snackbar from "@mui/material/Snackbar";
import {styled} from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import axios from "axios";
import {BASE_URL, GET} from "APIclient";
import {useLocation} from "react-router-dom";
import properties from "constants/ApplicationConstants";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

const ASPECT_RATIOS = [
  {label: "1:1", value: 1 / 1},
  {label: "4:3", value: 4 / 3},
  {label: "16:9", value: 16 / 9},
  {label: "3:4", value: 3 / 4}
];

async function getCroppedFile(imageSrc, pixelCrop, fileName) {
  const image = await createImageBitmap(await fetch(imageSrc).then(r => r.blob()));
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve(new File([blob], fileName || "cropped.jpg", {type: "image/jpeg"}));
    }, "image/jpeg");
  });
}

function UploadImage() {
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");

  const [rawImageSrc, setRawImageSrc] = useState(null);

  const [croppedFile, setCroppedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  const [hasUploadImagePermission, setHasUploadImagePermission] = useState(false);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({x: 0, y: 0});
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rawFileName, setRawFileName] = useState("photo.jpg");

  const location = useLocation();
  const empId = location.state || window.sessionStorage.getItem("empId");
  const url = `${BASE_URL}/employee/upload-img/${empId}`;
  const url1 = `employee/employee-details/${empId}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(url1);
        const data = response?.data?.data;
        if (data && response.data.statusCode === 200) {
          if (data.employee?.photo) {
            setPreviewUrl(`data:image/jpeg;base64,${data.employee.photo}`);
          }
          setHasUploadImagePermission(data.employee.permissions.includes("upload_image"));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  function handleChange(e) {
    if (!hasUploadImagePermission) return;
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setRawFileName(selectedFile.name);
    const objectUrl = URL.createObjectURL(selectedFile);
    setRawImageSrc(objectUrl);

    setCrop({x: 0, y: 0});
    setZoom(1);
    setAspect(1);
    setCropModalOpen(true);

    e.target.value = "";
  }

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function handleCropConfirm() {
    try {
      const file = await getCroppedFile(rawImageSrc, croppedAreaPixels, rawFileName);
      setCroppedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setIsEdited(true);
      setCropModalOpen(false);
    } catch (err) {
      console.error("Crop error:", err);
    }
  }

  function handleCropCancel() {
    setCropModalOpen(false);
    setRawImageSrc(null);
  }

  const handleClose = () => setOpen(false);

  async function handleUpload() {
    if (!hasUploadImagePermission) return;

    if (!croppedFile) {
      setSnakColor("error");
      setSnakMessage("Please select and crop a file first.");
      setOpen(true);
      return;
    }
    if (croppedFile.size > 1024 * 1024) {
      setSnakColor("error");
      setSnakMessage("File size exceeds 1MB. Please upload a smaller file.");
      setOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append("image", croppedFile);

    try {
      const response = await axios.patch(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        }
      });

      if (response?.status === 200) {
        setSnakColor("success");
        setSnakMessage("Image uploaded successfully");
        setOpen(true);
        setIsEdited(false);

        const updatedPhotoBase64 = response.data?.data?.photo;
        if (updatedPhotoBase64) {
          window.sessionStorage.setItem("photoBase64", updatedPhotoBase64);
          window.dispatchEvent(new Event("photoBase64Updated"));
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result.split(",")[1];
            window.sessionStorage.setItem("photoBase64", base64String);
            window.dispatchEvent(new Event("photoBase64Updated"));
          };
          reader.readAsDataURL(croppedFile);
        }
      }
    } catch (error) {
      setSnakMessage(error.response?.data?.message || "Failed to upload image.");
      setSnakColor("error");
      setOpen(true);
    }
  }

  return (
    <Container>
      <MDBox px={2}>
        <MDBox pt={2} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <InputLabel sx={{fontWeight: "bold", mb: 1}}>Image Upload</InputLabel>
              <MDButton
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
                sx={{justifyContent: "space-between"}}
              >
                Upload Image
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleChange}
                  accept="image/*"
                  disabled={!hasUploadImagePermission}
                />
              </MDButton>
            </MDBox>

            {previewUrl && (
              <MDBox
                component="img"
                src={previewUrl}
                loading="lazy"
                sx={{width: 280, height: 300, objectFit: "cover", border: "1px solid #cecece"}}
              />
            )}

            <MDBox mt={4} mb={1} sx={{textAlign: "right"}}>
              <MDButton
                variant="gradient"
                color="info"
                sx={{width: "30ch", background: properties.colors.bgcolorbluethem}}
                onClick={handleUpload}
                disabled={!hasUploadImagePermission || !isEdited}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>

          <Snackbar
            open={open}
            anchorOrigin={{vertical: "top", horizontal: "right"}}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <Alert
              onClose={handleClose}
              severity={snakColor === "success" ? "success" : "error"}
              variant="filled"
              sx={{width: "100%", color: "#fff"}}
            >
              {snakMessage}
            </Alert>
          </Snackbar>
        </MDBox>
      </MDBox>

      <Dialog
        open={cropModalOpen}
        onClose={handleCropCancel}
        maxWidth="sm"
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            height: "560px",
            maxHeight: "90vh"
          }
        }}
      >
        <DialogTitle
          sx={{display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1}}
        >
          <Typography variant="h6" fontWeight={600} fontFamily={properties.fontFamily}>
            Crop Image
          </Typography>
          <IconButton onClick={handleCropCancel} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{p: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
          <Box sx={{position: "relative", flex: 1, background: "#111", minHeight: 0}}>
            {rawImageSrc && (
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: {borderRadius: 0},
                  cropAreaStyle: {border: "2px solid #fff"}
                }}
              />
            )}
          </Box>

          <Box sx={{px: 3, pt: 2, pb: 1, flexShrink: 0}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
              <AspectRatio fontSize="small" sx={{color: "text.secondary"}} />
              <Typography variant="caption" color="text.secondary" sx={{mr: 1}}>
                Ratio:
              </Typography>
              {ASPECT_RATIOS.map(r => (
                <MDButton
                  key={r.label}
                  variant={aspect === r.value ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setAspect(r.value)}
                  sx={{minWidth: 0, px: 1.5, py: 0.5, fontSize: 12}}
                >
                  {r.label}
                </MDButton>
              ))}
            </Box>

            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
              <Typography variant="caption" color="text.secondary" sx={{minWidth: 40}}>
                Zoom
              </Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.05}
                onChange={(_, v) => setZoom(v)}
                sx={{flex: 1, color: properties.colors.bgcolorbluethem}}
              />
              <Typography variant="caption" color="text.secondary" sx={{minWidth: 32}}>
                {zoom.toFixed(1)}x
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{px: 3, pb: 2, gap: 1, flexShrink: 0}}>
          <MDButton variant="outlined" onClick={handleCropCancel} sx={{flex: 1}}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleCropConfirm}
            sx={{
              flex: 1,
              background: properties.colors.bgcolorbluethem,
              fontFamily: properties.fontFamily
            }}
          >
            Crop &amp; Apply
          </MDButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default UploadImage;
